import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { AppThunk } from 'store'
import { ApiAction, createApiAction } from 'api'
import { Person } from 'core/people/types'
import { userLoggedOut } from 'core/session/slices/session'
import { RangePeriod } from 'components/common/BigCalendar/types'
import { BigCalendarUtils } from 'components/common/BigCalendar/utils'
import { getVirtualEvent } from 'modules/virtualClassrooms/common/api/managerService'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import { VirtualEventRole } from 'modules/virtualClassrooms/common/types/manager'
import {
  getListSchedulesForCalendar,
  getListVirtualEventRoomSettings,
  addVirtualEventScheduleWithEnrollments,
} from 'modules/virtualClassrooms/manager/Schedules/api/schedulesService'
import scheduleRoutes from 'modules/virtualClassrooms/manager/Schedules/routing/routes'
import { VirtualEventsListSchedule } from 'modules/virtualClassrooms/manager/Schedules/types'
import {
  AddVirtualEventScheduleWithEnrollmentsModel,
  ScheduleEnrollment,
} from 'modules/virtualClassrooms/manager/Schedules/types/creator'
import { PlannerUtils } from 'modules/virtualClassrooms/manager/Schedules/utils'
import {
  CreatorState,
  ApiVirtualEventDetailsResponse,
  ApiListVirtualEventSchedulesResponse,
  ApiListVirtualEventRoomSettingsResponse,
  ApiAddVirtualEventScheduleWithEnrollmentsResponse,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:manager.schedules.creator`

const initialState: CreatorState = {
  eventDetailsData: {},
  scheduleList: {},
  roomSettings: {},
  scheduleCreatorData: {
    timeZone: undefined,
    range: BigCalendarUtils.getDateMonthPeriod(new Date()),
    createdSchedule: null,
    addedPresenters: {},
    addedParticipants: {},
  },
  createScheduleRequest: {},
}

const schedulesCreatorSlice = createSlice({
  name: 'virtualClassroomManagerSchedulesCreator',
  initialState,
  reducers: {
    eventDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiVirtualEventDetailsResponse>>
    ) {
      state.eventDetailsData = action.payload
    },
    listVirtualEventSchedulesLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventSchedulesResponse>>
    ) {
      state.scheduleList = action.payload
    },
    virtualEventRoomSettingsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventRoomSettingsResponse>>
    ) {
      state.roomSettings = action.payload
    },
    setTimeZone(state, action: PayloadAction<string | undefined>) {
      state.scheduleCreatorData.timeZone = action.payload
    },
    setRange(state, action: PayloadAction<RangePeriod>) {
      state.scheduleCreatorData.range = action.payload
    },
    setCreatedSchedule(state, action: PayloadAction<VirtualEventsListSchedule>) {
      state.scheduleCreatorData.createdSchedule = action.payload
    },
    removeCreatedSchedule(state) {
      state.scheduleCreatorData.createdSchedule = initialState.scheduleCreatorData.createdSchedule
    },
    removePresenters(state, action: PayloadAction<Record<string, Person>>) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleCreatorData.addedPresenters[studentId]
      })
    },
    addPresenters(state, action: PayloadAction<Record<string, Person>>) {
      state.scheduleCreatorData.addedPresenters = {
        ...state.scheduleCreatorData.addedPresenters,
        ...action.payload,
      }
    },
    removeParticipants(state, action: PayloadAction<Record<string, Person>>) {
      Object.keys(action.payload).forEach(studentId => {
        delete state.scheduleCreatorData.addedParticipants[studentId]
      })
    },
    addParticipants(state, action: PayloadAction<Record<string, Person>>) {
      state.scheduleCreatorData.addedParticipants = {
        ...state.scheduleCreatorData.addedParticipants,
        ...action.payload,
      }
    },
    clearCreatorData(state) {
      state.eventDetailsData = initialState.eventDetailsData
      state.scheduleList = initialState.scheduleList
      state.scheduleCreatorData = initialState.scheduleCreatorData
    },
    createScheduleRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiAddVirtualEventScheduleWithEnrollmentsResponse>>
    ) {
      state.createScheduleRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  eventDetailsLoaded,
  listVirtualEventSchedulesLoaded,
  virtualEventRoomSettingsLoaded,
  createScheduleRequested,
} = schedulesCreatorSlice.actions
export const {
  setTimeZone,
  setRange,
  setCreatedSchedule,
  removeCreatedSchedule,
  addPresenters,
  removePresenters,
  addParticipants,
  removeParticipants,
  clearCreatorData,
} = schedulesCreatorSlice.actions
export default schedulesCreatorSlice.reducer

export const loadEventDetailsData = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEvent(eventId),
    },
    payloadAction: eventDetailsLoaded,
  })
}

export const handleLoadListVirtualEventRoomSettings =
  (response: ApiMiddlewarePayload<ApiListVirtualEventRoomSettingsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      PlannerUtils.setResources(result.settings.roomSettings)
    }
    dispatch(virtualEventRoomSettingsLoaded(response))
  }

export const loadListVirtualEventRoomSettings = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      settings: () => getListVirtualEventRoomSettings(),
    },
    payloadAction: virtualEventRoomSettingsLoaded,
    nextAction: handleLoadListVirtualEventRoomSettings,
    shouldCallApi: () => !PlannerUtils.resources.length,
  })
}

export const loadListVirtualEventSchedules = (range: RangePeriod, timeZone: String): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSchedulesForCalendar(range, timeZone),
    },
    payloadAction: listVirtualEventSchedulesLoaded,
  })
}

export const reloadListVirtualEventSchedules = (
  range: RangePeriod,
  timeZone: String
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSchedulesForCalendar(range, timeZone),
    },
    payloadAction: listVirtualEventSchedulesLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ virtualClassroomManagerSchedulesCreator }) =>
      virtualClassroomManagerSchedulesCreator.scheduleList.result,
  })
}

export const getScheduleMappedToPlanner = createSelector(
  (scheduleList: VirtualEventsListSchedule[] = []) => ({
    scheduleList,
  }),
  ({ scheduleList }) => PlannerUtils.mapScheduleToPlanner(scheduleList)
)

export const getMergedScheduleListEvents = createSelector(
  (scheduleList: VirtualEventsListSchedule[] = [], event: Nullable<VirtualEventsListSchedule>) => ({
    scheduleList,
    event,
  }),
  ({ scheduleList, event }) => {
    if (event) return [...scheduleList, event]
    return scheduleList
  }
)

export const getAddedPersonsParticipants = createSelector(
  (persons: Record<string, Person>) => persons,
  persons => Object.values(persons)
)

export const getAddedPersonsPresenters = createSelector(
  (persons: Record<string, Person>) => persons,
  persons => Object.values(persons)
)

const handleCreateVirtualEventSchedule =
  (
    response: ApiMiddlewarePayload<ApiAddVirtualEventScheduleWithEnrollmentsResponse>,
    eventId: Guid
  ): AppThunk =>
  async dispatch => {
    const { error, result } = response
    dispatch(createScheduleRequested(response))
    if (!error && result) {
      dispatch(push(`${scheduleRoutes.list}/${eventId}`))
    }
  }

export const createVirtualEventScheduleWithEnrollments = (
  params: AddVirtualEventScheduleWithEnrollmentsModel
): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventScheduleWithEnrollments(params),
    },
    payloadAction: createScheduleRequested,
    nextAction: {
      action: handleCreateVirtualEventSchedule,
      params: params.eventId,
    },
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    successMessage: i18n.t(`${I18_PATH}.successBook`),
  })
}

export const getScheduleEnrollments = createSelector(
  (addedPresenters: Record<string, Person>, addedParticipants: Record<string, Person>) => ({
    addedPresenters,
    addedParticipants,
  }),
  ({ addedPresenters, addedParticipants }) => {
    const enrolledPresenters: ScheduleEnrollment[] = Object.keys(addedPresenters).map(userId => ({
      userId,
      role: VirtualEventRole.Presenter,
    }))
    const enrolledParticipants: ScheduleEnrollment[] = Object.keys(addedParticipants).map(
      userId => ({ userId, role: VirtualEventRole.Listener })
    )
    return [...enrolledPresenters, ...enrolledParticipants]
  }
)
